import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  // UI State
  activeTab: 'inventory',
  isFullscreen: false,
  
  // Selected Items in 3D Scene
  selectedItems: [],
  
  // Generated 3D Models
  generatedModels: [],
  
  // Store Layout Configuration
  storeLayout: {
    floorSize: 20,
    shelves: []
  },
  
  // 3D Viewer Settings
  viewerSettings: {
    environment: 'warehouse',
    environmentIntensity: 1,
    shadows: true,
    ambientIntensity: 0.4,
    directionalIntensity: 1,
    showGrid: true,
    showLabels: true,
    showPrices: false,
    labelSize: 1,
    quality: 'medium',
    antialiasing: true,
    renderDistance: 100
  },
  
  // History for undo/redo
  history: {
    past: [],
    future: []
  }
};

export const useStorefront3DStore = create(
  devtools(
    (set, get) => ({
      ...initialState,
      
      // UI Actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      setIsFullscreen: (fullscreen) => set({ isFullscreen: fullscreen }),
      
      // Item Management
      addItem: (item) => set((state) => ({
        selectedItems: [...state.selectedItems, { ...item, id: item.id || Date.now() }]
      })),
      
      removeItem: (itemId) => set((state) => ({
        selectedItems: state.selectedItems.filter(item => item.id !== itemId)
      })),
      
      updateItem: (itemId, updates) => set((state) => ({
        selectedItems: state.selectedItems.map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        )
      })),
      
      toggleItemVisibility: (itemId) => set((state) => ({
        selectedItems: state.selectedItems.map(item =>
          item.id === itemId ? { ...item, visible: !item.visible } : item
        )
      })),
      
      clearItems: () => set({ selectedItems: [] }),
      
      // Generated Models Management
      addGeneratedModel: (model) => set((state) => ({
        generatedModels: [...state.generatedModels, { ...model, id: model.id || Date.now() }]
      })),
      
      removeGeneratedModel: (modelId) => set((state) => ({
        generatedModels: state.generatedModels.filter(model => model.id !== modelId)
      })),
      
      updateGeneratedModel: (modelId, updates) => set((state) => ({
        generatedModels: state.generatedModels.map(model =>
          model.id === modelId ? { ...model, ...updates } : model
        )
      })),
      
      clearGeneratedModels: () => set({ generatedModels: [] }),
      
      // Store Layout Management
      updateStoreLayout: (layout) => set({ storeLayout: layout }),
      
      addShelf: (shelf) => set((state) => ({
        storeLayout: {
          ...state.storeLayout,
          shelves: [...state.storeLayout.shelves, shelf]
        }
      })),
      
      removeShelf: (shelfId) => set((state) => ({
        storeLayout: {
          ...state.storeLayout,
          shelves: state.storeLayout.shelves.filter(shelf => shelf.id !== shelfId)
        }
      })),
      
      updateShelf: (shelfId, updates) => set((state) => ({
        storeLayout: {
          ...state.storeLayout,
          shelves: state.storeLayout.shelves.map(shelf =>
            shelf.id === shelfId ? { ...shelf, ...updates } : shelf
          )
        }
      })),
      
      // Viewer Settings
      updateViewerSettings: (settings) => set((state) => ({
        viewerSettings: { ...state.viewerSettings, ...settings }
      })),
      
      resetToDefaults: () => set({
        viewerSettings: initialState.viewerSettings,
        storeLayout: initialState.storeLayout,
        selectedItems: [],
        generatedModels: []
      }),
      
      // History Management
      saveToHistory: () => set((state) => {
        const currentState = {
          selectedItems: state.selectedItems,
          storeLayout: state.storeLayout
        };
        
        return {
          history: {
            past: [...state.history.past, currentState].slice(-50), // Keep last 50 states
            future: []
          }
        };
      }),
      
      undo: () => set((state) => {
        if (state.history.past.length === 0) return state;
        
        const previous = state.history.past[state.history.past.length - 1];
        const newPast = state.history.past.slice(0, -1);
        
        const currentState = {
          selectedItems: state.selectedItems,
          storeLayout: state.storeLayout
        };
        
        return {
          ...previous,
          history: {
            past: newPast,
            future: [currentState, ...state.history.future]
          }
        };
      }),
      
      redo: () => set((state) => {
        if (state.history.future.length === 0) return state;
        
        const next = state.history.future[0];
        const newFuture = state.history.future.slice(1);
        
        const currentState = {
          selectedItems: state.selectedItems,
          storeLayout: state.storeLayout
        };
        
        return {
          ...next,
          history: {
            past: [...state.history.past, currentState],
            future: newFuture
          }
        };
      }),
      
      canUndo: () => get().history.past.length > 0,
      canRedo: () => get().history.future.length > 0,
      
      // Utility Actions
      exportScene: () => {
        const state = get();
        return {
          selectedItems: state.selectedItems,
          storeLayout: state.storeLayout,
          viewerSettings: state.viewerSettings,
          exportedAt: new Date().toISOString()
        };
      },
      
      importScene: (sceneData) => set({
        selectedItems: sceneData.selectedItems || [],
        storeLayout: sceneData.storeLayout || initialState.storeLayout,
        viewerSettings: { ...initialState.viewerSettings, ...sceneData.viewerSettings }
      })
    }),
    {
      name: 'storefront3d-store',
      partialize: (state) => ({
        viewerSettings: state.viewerSettings,
        storeLayout: state.storeLayout
      })
    }
  )
);
